import { formatDollars } from '@fruitful/fruitils';
import type { ChartValueFormat } from '@fruitful/types';

export const CHART_HEIGHT = 150;
export const CHART_Y_SECTIONS = 4;
export const X_AXIS_LABEL_LINES = 2;
export const BAR_INITIAL_SPACING = 12;
export const Y_AXIS_LABEL_WIDTH = 40;
export const Y_AXIS_LABEL_WIDTH_CURRENCY = 56;

const MIN_BAR_WIDTH = 16;
const MAX_BAR_WIDTH = 40;
const MIN_BAR_SPACING = 8;
const MAX_BAR_SPACING = 24;

/** Currency series values are stored in cents; chart axes plot dollars. */
export const toChartValue = (
    value: number,
    format: ChartValueFormat,
): number => (format === 'currency' ? value / 100 : value);

export const formatValue = (
    value: number,
    format: ChartValueFormat,
): string => {
    if (format === 'currency') {
        return formatDollars(value / 100, false);
    }

    if (format === 'percent') {
        return `${value.toString()}%`;
    }

    return value.toLocaleString();
};

/** Format a value already in chart/display units (dollars for currency). */
export const formatAxisValue = (
    value: number,
    format: ChartValueFormat,
): string => {
    if (format === 'currency') {
        return formatDollars(value, false);
    }

    if (format === 'percent') {
        return `${Math.round(value).toString()}%`;
    }

    return Number.isInteger(value)
        ? value.toLocaleString()
        : value.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

/** Round up to a 1 / 2 / 5 × 10^n ceiling for clean axis ticks. */
const niceCeiling = (value: number): number => {
    if (value <= 0) {
        return 1;
    }

    const exponent = Math.floor(Math.log10(value));
    const magnitude = 10 ** exponent;
    const fraction = value / magnitude;
    const niceFraction =
        fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;

    return niceFraction * magnitude;
};

export const getAxisMax = (chartValues: number[], sections: number): number => {
    const dataMax = Math.max(...chartValues, 0);
    if (dataMax === 0) {
        return sections;
    }

    const step = niceCeiling(dataMax / sections);
    return step * sections;
};

export const getYAxisLabelWidth = (valueFormat: ChartValueFormat): number =>
    valueFormat === 'currency'
        ? Y_AXIS_LABEL_WIDTH_CURRENCY
        : Y_AXIS_LABEL_WIDTH;

export const getBarLayout = (
    chartWidth: number,
    pointCount: number,
    yAxisLabelWidth: number,
) => {
    const available = chartWidth - BAR_INITIAL_SPACING - yAxisLabelWidth - 8;
    const count = Math.max(pointCount, 1);
    const slot = available / count;
    const barWidth = Math.min(
        MAX_BAR_WIDTH,
        Math.max(MIN_BAR_WIDTH, Math.floor(slot * 0.55)),
    );
    const spacing = Math.min(
        MAX_BAR_SPACING,
        Math.max(MIN_BAR_SPACING, Math.floor(slot - barWidth)),
    );

    return { barWidth, spacing };
};
